/**
 * Lossback History Page (รายการยอดเสีย)
 * 
 * หน้าแสดงรายการยอดเสียรายวัน
 * - แสดงยอดเสียต่อวันออกมาให้เห็นชัดเจน
 * - ค้นหาเลือกช่วงเวลา
 * - เลือกดูรายคนได้
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  CheckCircle,
  RefreshCw,
  BarChart3,
  ChevronRight,
  ChevronDown,
  Gamepad2,
  Eye,
  Code,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import {
  WinloseService,
  type Winlose,
  type WinloseQueryParams,
} from '@/domain/winlose'
import { GAME_GROUPS } from '@/domain/return-lose'
import moment from 'moment'
import { ThaiDatePicker } from '@/components/ui/date-time-picker'
import { toDateInputValue } from '@/lib/date-format'

// ============================================
// Helper Functions
// ============================================

function formatNumber(value: number, decimals: number = 2): string {
  return value.toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}



// ============================================
// Main Component
// ============================================

export function LossbackHistoryPage() {
  const { sessionToken } = useAuth()
  
  // Winlose state
  const [isLoadingWinlose, setIsLoadingWinlose] = useState(true)
  const [winloseList, setWinloseList] = useState<Winlose[]>([])
  const [, setWinloseTotal] = useState(0) // Total count (for future use)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  
  // Filters
  const [dateRange, setDateRange] = useState<{
    start: string
    end: string
  }>({
    start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD'),
  })
  const [memberSearch, setMemberSearch] = useState('')
  const [winlosePage, setWinlosePage] = useState(1)
  const [winloseLimit, setWinloseLimit] = useState(50)
  const [selectedGames, setSelectedGames] = useState<string[]>([]) // เกมที่เลือกแสดง
  const [gameFilter, setGameFilter] = useState<string>('all') // filter ตามเกม (all = แสดงทั้งหมด)
  
  // Dialogs
  const [winloseDetailDialogOpen, setWinloseDetailDialogOpen] = useState(false)
  const [selectedWinlose, setSelectedWinlose] = useState<Winlose | null>(null)

  // ============================================
  // Load Winlose Data
  // ============================================

  const loadWinloseData = useCallback(async () => {
    if (!sessionToken) return

    setIsLoadingWinlose(true)
    try {
      // Convert YYYY-MM-DD to DD-MM-YYYY
      const formatDateForParse = (dateStr: string) => {
        if (!dateStr) return undefined
        const [year, month, day] = dateStr.split('-')
        return `${day}-${month}-${year}`
      }

      // ตรวจสอบว่ามีวันที่ครบถ้วนหรือไม่
      if (!dateRange.start || !dateRange.end) {
        console.warn('Date range is incomplete')
        setWinloseList([])
        setWinloseTotal(0)
        return
      }

      const params: WinloseQueryParams = {
        page: winlosePage,
        limit: winloseLimit,
        query: memberSearch || undefined,
        date_range: {
          start: formatDateForParse(dateRange.start)!,
          end: formatDateForParse(dateRange.end)!,
        },
      }
      
      console.log('[Winlose] Query params:', params)
      const result = await WinloseService.findWinlose(sessionToken, params)
      
      console.log('[Winlose] Results:', result.items.length, 'items, total:', result.total)
      
      // Debug: ตรวจสอบ winlose_obj.data
      if (result.items.length > 0) {
        const firstItem = result.items[0]
        console.log('[Winlose] First item winlose_obj:', firstItem.winlose_obj)
        console.log('[Winlose] First item winlose_obj.data:', firstItem.winlose_obj?.data)
        if (firstItem.winlose_obj?.data) {
          const gameKeys = Object.keys(firstItem.winlose_obj.data)
          console.log('[Winlose] Available games in data:', gameKeys)
        }
      }
      
      setWinloseList(result.items)
      setWinloseTotal(result.total)
    } catch (err) {
      console.error('Error loading winlose data:', err)
      setWinloseList([])
      setWinloseTotal(0)
    } finally {
      setIsLoadingWinlose(false)
    }
  }, [sessionToken, winlosePage, winloseLimit, memberSearch, dateRange])

  useEffect(() => {
    loadWinloseData()
  }, [loadWinloseData])

  // Helper: คำนวณยอด win/lose จาก winlose_obj
  const calculateGameWinlose = (winlose: Winlose) => {
    const data = winlose.winlose_obj?.data || {}
    let totalWl = 0
    let totalVl = 0
    let totalCom = 0
    let totalStake = 0

    Object.values(data).forEach((game: any) => {
      if (game && typeof game === 'object') {
        totalWl += game.wl || 0
        totalVl += game.vl || 0
        totalCom += game.com || 0
        totalStake += game.stake || 0
      }
    })

    return { totalWl, totalVl, totalCom, totalStake }
  }

  // Helper: รับรายการเกมที่มีข้อมูล
  const getGameList = (winlose: Winlose) => {
    const data = winlose.winlose_obj?.data || {}
    return Object.entries(data)
      .filter(([_, gameData]: [string, any]) => gameData && typeof gameData === 'object')
      .map(([gameKey, gameData]: [string, any]) => ({
        key: gameKey,
        label: GAME_GROUPS.find(g => g.value === gameKey)?.label || gameKey,
        ...gameData,
      }))
      .filter(game => game.wl !== 0 || game.vl !== 0 || game.com !== 0 || game.stake !== 0) // แสดงเฉพาะที่มีข้อมูล
  }

  // Helper: รับเกมทั้งหมดที่มีข้อมูลใน winloseList
  const getAllAvailableGames = useMemo(() => {
    const gameSet = new Set<string>()
    winloseList.forEach(item => {
      const data = item.winlose_obj?.data || {}
      Object.keys(data).forEach(gameKey => {
        const gameData = data[gameKey]
        if (gameData && typeof gameData === 'object' && (gameData.wl !== 0 || gameData.vl !== 0 || gameData.com !== 0 || gameData.stake !== 0)) {
          gameSet.add(gameKey)
        }
      })
    })
    // เรียงตามลำดับใน GAME_GROUPS
    const sorted = Array.from(gameSet).sort((a, b) => {
      const indexA = GAME_GROUPS.findIndex(g => g.value === a)
      const indexB = GAME_GROUPS.findIndex(g => g.value === b)
      if (indexA === -1 && indexB === -1) return a.localeCompare(b)
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
    console.log('[Winlose] getAllAvailableGames:', sorted.length, sorted)
    return sorted
  }, [winloseList])

  // เกมที่เลือกแสดง (ถ้า selectedGames ว่าง = แสดงทั้งหมดที่มีข้อมูล)
  const displayGames = useMemo(() => {
    let games: string[] = []
    
    if (selectedGames.length === 0) {
      // ถ้ายังไม่มีข้อมูล ให้แสดงเกมหลักๆ ก่อน (เพื่อให้เห็น structure)
      if (getAllAvailableGames.length === 0) {
        // ถ้ายังไม่มีข้อมูลเลย ให้แสดงเกมหลักๆ เพื่อให้เห็น structure
        // แต่จะแสดงเฉพาะเมื่อมีข้อมูล winloseList
        if (winloseList.length > 0) {
          // มีข้อมูลแต่ไม่มีเกมที่มีค่า != 0 -> แสดงเกมหลักๆ
          games = ['hdp', 'casino', 'slot', 'mixParlay', 'mixStep', 'card', 'lotto', 'keno']
        } else {
          // ยังไม่มีข้อมูลเลย -> ไม่แสดงเกม
          games = []
        }
      } else {
        // แสดงเกมทั้งหมดที่มีข้อมูล
        games = getAllAvailableGames
      }
    } else {
      // แสดงเฉพาะเกมที่เลือก (รวมเกมที่อาจไม่มีข้อมูลด้วย)
      games = selectedGames
    }
    
    console.log('[Winlose] displayGames:', games.length, games)
    return games
  }, [selectedGames, getAllAvailableGames, winloseList.length])

  // Filter ข้อมูลตามเกมที่เลือก
  const filteredWinloseList = useMemo(() => {
    if (gameFilter === 'all') {
      return winloseList
    }
    return winloseList.filter(item => {
      const data = item.winlose_obj?.data || {}
      const gameData = data[gameFilter]
      return gameData && (gameData.wl !== 0 || gameData.vl !== 0)
    })
  }, [winloseList, gameFilter])

  const toggleRow = (objectId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(objectId)) {
        newSet.delete(objectId)
      } else {
        newSet.add(objectId)
      }
      return newSet
    })
  }


  // ============================================
  // Quick Date Filters
  // ============================================

  const setQuickDateRange = (range: 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth') => {
    const today = moment()
    switch (range) {
      case 'today':
        setDateRange({
          start: today.format('YYYY-MM-DD'),
          end: today.format('YYYY-MM-DD'),
        })
        break
      case 'yesterday':
        const yesterday = today.clone().subtract(1, 'day')
        setDateRange({
          start: yesterday.format('YYYY-MM-DD'),
          end: yesterday.format('YYYY-MM-DD'),
        })
        break
      case 'last7':
        setDateRange({
          start: today.clone().subtract(7, 'days').format('YYYY-MM-DD'),
          end: today.format('YYYY-MM-DD'),
        })
        break
      case 'last30':
        setDateRange({
          start: today.clone().subtract(30, 'days').format('YYYY-MM-DD'),
          end: today.format('YYYY-MM-DD'),
        })
        break
      case 'thisMonth':
        setDateRange({
          start: today.clone().startOf('month').format('YYYY-MM-DD'),
          end: today.format('YYYY-MM-DD'),
        })
        break
      case 'lastMonth':
        const lastMonth = today.clone().subtract(1, 'month')
        setDateRange({
          start: lastMonth.startOf('month').format('YYYY-MM-DD'),
          end: lastMonth.endOf('month').format('YYYY-MM-DD'),
        })
        break
    }
  }


  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">รายการยอดเสีย</h1>
          <p className="text-sm text-muted-foreground mt-1">
            ดูรายการยอดเสีย (Winlose) จาก WinLose collection
          </p>
        </div>
        <Button 
          onClick={loadWinloseData} 
          variant="outline" 
          size="sm"
          disabled={isLoadingWinlose}
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", isLoadingWinlose && "animate-spin")} />
          รีเฟรช
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <Label>ช่วงเวลา</Label>
              <div className="flex gap-2">
                <ThaiDatePicker
                  selected={dateRange.start ? new Date(dateRange.start) : null}
                  onChange={(date) => setDateRange(prev => ({ ...prev, start: date ? toDateInputValue(date) : '' }))}
                  placeholder="วันที่เริ่มต้น"
                />
                <ThaiDatePicker
                  selected={dateRange.end ? new Date(dateRange.end) : null}
                  onChange={(date) => setDateRange(prev => ({ ...prev, end: date ? toDateInputValue(date) : '' }))}
                  placeholder="วันที่สิ้นสุด"
                />
              </div>
            </div>

            {/* Quick Date Filters */}
            <div className="space-y-2">
              <Label>ตัวเลือกเร็ว</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value) setQuickDateRange(value as any)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือกช่วงเวลา" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">วันนี้</SelectItem>
                  <SelectItem value="yesterday">เมื่อวาน</SelectItem>
                  <SelectItem value="last7">7 วันล่าสุด</SelectItem>
                  <SelectItem value="last30">30 วันล่าสุด</SelectItem>
                  <SelectItem value="thisMonth">เดือนนี้</SelectItem>
                  <SelectItem value="lastMonth">เดือนที่แล้ว</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Member Search */}
            <div className="space-y-2">
              <Label>ค้นหาสมาชิก</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="username, ชื่อ..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Limit */}
            <div className="space-y-2">
              <Label>แสดงต่อหน้า</Label>
              <Select
                value={String(winloseLimit)}
                onValueChange={(v) => { setWinloseLimit(Number(v)); setWinlosePage(1) }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Game Filter */}
          <div className="mt-4 space-y-2">
            <Label>Filter ตามเกม</Label>
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger>
                <SelectValue placeholder="เลือกเกม" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                {GAME_GROUPS.map((game) => (
                  <SelectItem key={game.value} value={game.value}>
                    {game.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Select Games to Display */}
          <div className="mt-4 space-y-2">
            <Label>เลือกเกมที่ต้องการแสดง (คลิกเพื่อเลือก/ยกเลิก) - {getAllAvailableGames.length} เกมที่มีข้อมูล</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30 max-h-40 overflow-y-auto">
              {GAME_GROUPS.map((game) => {
                const isSelected = selectedGames.includes(game.value)
                const isAvailable = getAllAvailableGames.includes(game.value)
                const isDisplayed = displayGames.includes(game.value)
                return (
                  <button
                    key={game.value}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedGames(prev => prev.filter(g => g !== game.value))
                      } else {
                        setSelectedGames(prev => [...prev, game.value])
                      }
                    }}
                    className={cn(
                      "px-3 py-1.5 text-xs rounded-md border transition-colors relative",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary"
                        : isDisplayed
                        ? "bg-blue-500/10 text-blue-700 border-blue-500/30"
                        : "bg-background hover:bg-muted border-border",
                      !isAvailable && "opacity-30"
                    )}
                    title={!isAvailable ? "ไม่มีข้อมูล" : isDisplayed ? "กำลังแสดง" : "คลิกเพื่อแสดง"}
                  >
                    {game.label}
                    {isSelected && " ✓"}
                    {isDisplayed && !isSelected && " •"}
                  </button>
                )
              })}
              {selectedGames.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedGames([])}
                  className="px-3 py-1.5 text-xs rounded-md border border-destructive text-destructive hover:bg-destructive/10"
                >
                  ล้างทั้งหมด
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedGames.length === 0 
                ? getAllAvailableGames.length > 0
                  ? `แสดงทั้งหมด ${displayGames.length} เกมที่มีข้อมูล (คลิกเพื่อเลือกเกมที่ต้องการแสดง)`
                  : winloseList.length > 0
                    ? `แสดง ${displayGames.length} เกม (ไม่มีข้อมูลในเกมใดๆ - แสดงโครงสร้างเท่านั้น)`
                    : 'ยังไม่มีข้อมูล - เลือกช่วงวันที่เพื่อดูข้อมูล'
                : `แสดง ${selectedGames.length} เกม: ${selectedGames.map(g => GAME_GROUPS.find(gg => gg.value === g)?.label || g).join(', ')}`
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Table - Winlose List */}
      <Card className="border-zinc-200/50 dark:border-zinc-800">
        <CardContent className="p-0">
          {isLoadingWinlose ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredWinloseList.length === 0 ? (
            <div className="p-12 text-center">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-sm text-muted-foreground">ไม่พบรายการยอดเสีย</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead className="min-w-[150px]">สมาชิก</TableHead>
                      <TableHead className="min-w-[100px]">วันที่</TableHead>
                      {/* Dynamic Game Columns - แสดงเสมอ */}
                      {displayGames.length > 0 ? (
                        displayGames.map((gameKey) => {
                          const gameInfo = GAME_GROUPS.find(g => g.value === gameKey)
                          return (
                            <TableHead key={gameKey} className="text-center min-w-[120px]">
                              <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold">{gameInfo?.label || gameKey}</span>
                                <div className="flex gap-2 text-[10px] text-muted-foreground justify-center">
                                  <span>WL</span>
                                  <span>VL</span>
                                </div>
                              </div>
                            </TableHead>
                          )
                        })
                      ) : (
                        <>
                          <TableHead className="text-right min-w-[100px]">Win/Lose</TableHead>
                          <TableHead className="text-right min-w-[100px]">Valid Amount</TableHead>
                        </>
                      )}
                      <TableHead className="text-right min-w-[100px]">Balance</TableHead>
                      <TableHead className="text-center min-w-[80px]">ใช้ถอน</TableHead>
                      <TableHead className="text-center min-w-[80px]">ใช้คืนยอด</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWinloseList
                      .slice((winlosePage - 1) * winloseLimit, winlosePage * winloseLimit)
                      .map((item, index) => {
                      const { totalWl, totalVl } = calculateGameWinlose(item) // totalCom, totalStake unused
                      const isExpanded = expandedRows.has(item.objectId)
                      const gameList = getGameList(item)
                      
                      return (
                        <>
                          <TableRow 
                            key={item.objectId} 
                            className="hover:bg-muted/50 cursor-pointer"
                            onClick={() => toggleRow(item.objectId)}
                          >
                            <TableCell>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleRow(item.objectId)
                                }}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {(winlosePage - 1) * winloseLimit + index + 1}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.member_id?.agent_username || item.member_id?.username || item.member_id_str}</p>
                                {item.member_id?.firstname && (
                                  <p className="text-xs text-muted-foreground">
                                    {item.member_id.firstname} {item.member_id.lastname}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="font-mono">{item.start_date}</p>
                                {item.end_date !== item.start_date && (
                                  <p className="text-xs text-muted-foreground">ถึง {item.end_date}</p>
                                )}
                              </div>
                            </TableCell>
                            
                            {/* Dynamic Game Columns - แสดงเสมอ */}
                            {displayGames.length > 0 ? (
                              displayGames.map((gameKey) => {
                                // ตรวจสอบว่า winlose_obj.data มีอยู่จริงหรือไม่
                                const data = item.winlose_obj?.data
                                const gameData = (data && typeof data === 'object' && data[gameKey]) 
                                  ? data[gameKey] 
                                  : { wl: 0, vl: 0, com: 0, stake: 0 }
                                
                                // Debug: log first item's game data
                                if (index === 0 && gameKey === displayGames[0]) {
                                  console.log(`[Winlose] Game ${gameKey} data:`, gameData)
                                  console.log(`[Winlose] winlose_obj.data type:`, typeof data)
                                  console.log(`[Winlose] winlose_obj.data keys:`, data ? Object.keys(data) : 'no data')
                                }
                                
                                return (
                                  <TableCell key={gameKey} className="text-center">
                                    <div className="flex gap-2 justify-center">
                                      <span className={cn(
                                        "font-mono text-xs font-bold",
                                        (gameData.wl || 0) >= 0 ? "text-emerald-600" : "text-red-600"
                                      )}>
                                        {formatNumber(gameData.wl || 0)}
                                      </span>
                                      <span className="font-mono text-xs text-blue-600">
                                        {formatNumber(gameData.vl || 0)}
                                      </span>
                                    </div>
                                  </TableCell>
                                )
                              })
                            ) : (
                              <>
                                <TableCell className={cn(
                                  "text-right font-mono font-bold",
                                  totalWl >= 0 ? "text-emerald-600" : "text-red-600"
                                )}>
                                  {formatNumber(totalWl)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-blue-600">
                                  {formatNumber(totalVl)}
                                </TableCell>
                              </>
                            )}
                            
                            <TableCell className="text-right font-mono text-green-600 font-bold">
                              {formatNumber(item.balance)}
                            </TableCell>
                            <TableCell className="text-center">
                              {item.used_for_withdraw === 1 ? (
                                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  ใช้แล้ว
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  ยังไม่ใช้
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {item.used_for_retrun_lose === 1 ? (
                                <Badge variant="outline" className="bg-pink-500/10 text-pink-600 border-pink-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  ใช้แล้ว
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  ยังไม่ใช้
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Row - แสดงรายละเอียดแต่ละเกม */}
                          {isExpanded && (
                            <TableRow className="bg-muted/30">
                              <TableCell colSpan={(displayGames.length > 0 ? displayGames.length : 2) + 6} className="p-4">
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <Gamepad2 className="w-4 h-4 text-blue-500" />
                                      <span className="font-semibold text-sm">รายละเอียดตามกลุ่มเกม</span>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setSelectedWinlose(item)
                                        setWinloseDetailDialogOpen(true)
                                      }}
                                      className="gap-2"
                                    >
                                      <Eye className="w-4 h-4" />
                                      ดู Object ทั้งหมด
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                    {gameList.map((game) => (
                                      <Card key={game.key} className="border-zinc-200/50 dark:border-zinc-800">
                                        <CardContent className="p-3">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-sm">{game.label}</span>
                                            <Badge 
                                              variant="outline" 
                                              className={cn(
                                                "text-xs",
                                                game.wl >= 0 ? "text-emerald-600 border-emerald-500/30" : "text-red-600 border-red-500/30"
                                              )}
                                            >
                                              {game.wl >= 0 ? '+' : ''}{formatNumber(game.wl)}
                                            </Badge>
                                          </div>
                                          <div className="space-y-1 text-xs">
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">Valid:</span>
                                              <span className="font-mono text-blue-600">{formatNumber(game.vl)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">Commission:</span>
                                              <span className="font-mono text-amber-600">{formatNumber(game.com)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">Stake:</span>
                                              <span className="font-mono text-purple-600">{formatNumber(game.stake)}</span>
                                            </div>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </div>
                                  {gameList.length === 0 && (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                      ไม่มีข้อมูลเกม
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    แสดง {((winlosePage - 1) * winloseLimit) + 1} - {Math.min(winlosePage * winloseLimit, filteredWinloseList.length)} จาก {filteredWinloseList.length.toLocaleString()} รายการ
                    {gameFilter !== 'all' && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        Filter: {GAME_GROUPS.find(g => g.value === gameFilter)?.label || gameFilter}
                      </Badge>
                    )}
                    {selectedGames.length > 0 && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        แสดง {selectedGames.length} เกม
                      </Badge>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setWinlosePage(1)}
                    disabled={winlosePage === 1 || isLoadingWinlose}
                  >
                    หน้าแรก
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setWinlosePage(p => Math.max(1, p - 1))}
                    disabled={winlosePage <= 1 || isLoadingWinlose}
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                  </Button>
                  <span className="text-sm px-2 min-w-[100px] text-center">
                    หน้า {winlosePage} / {Math.ceil(filteredWinloseList.length / winloseLimit) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setWinlosePage(p => p + 1)}
                    disabled={winlosePage >= Math.ceil(filteredWinloseList.length / winloseLimit) || isLoadingWinlose}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setWinlosePage(Math.ceil(filteredWinloseList.length / winloseLimit))}
                    disabled={winlosePage >= Math.ceil(filteredWinloseList.length / winloseLimit) || isLoadingWinlose}
                  >
                    หน้าสุดท้าย
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Winlose Detail Dialog */}
      <Dialog open={winloseDetailDialogOpen} onOpenChange={setWinloseDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              Winlose Object Details
            </DialogTitle>
            <DialogDescription>
              {selectedWinlose && (
                <>
                  สมาชิก: {selectedWinlose.member_id?.agent_username || selectedWinlose.member_id_str} | 
                  วันที่: {selectedWinlose.start_date} {selectedWinlose.end_date !== selectedWinlose.start_date && `ถึง ${selectedWinlose.end_date}`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedWinlose && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-4">
                {/* Summary Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">ข้อมูลสรุป</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Balance</p>
                        <p className="font-mono font-bold text-green-600">{formatNumber(selectedWinlose.balance)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Sum WL</p>
                        <p className={cn(
                          "font-mono font-bold",
                          selectedWinlose.sum_wl >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {formatNumber(selectedWinlose.sum_wl)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Sum VL</p>
                        <p className="font-mono font-bold text-blue-600">{formatNumber(selectedWinlose.sum_vl)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Sum COM</p>
                        <p className="font-mono font-bold text-amber-600">{formatNumber(selectedWinlose.sum_com)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Sum Stake</p>
                        <p className="font-mono font-bold text-purple-600">{formatNumber(selectedWinlose.sum_stake)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">ใช้ถอน</p>
                        <Badge variant={selectedWinlose.used_for_withdraw === 1 ? "default" : "outline"}>
                          {selectedWinlose.used_for_withdraw === 1 ? "ใช้แล้ว" : "ยังไม่ใช้"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">ใช้คืนยอด</p>
                        <Badge variant={selectedWinlose.used_for_retrun_lose === 1 ? "default" : "outline"}>
                          {selectedWinlose.used_for_retrun_lose === 1 ? "ใช้แล้ว" : "ยังไม่ใช้"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">No per Day</p>
                        <p className="font-mono font-bold">{selectedWinlose.no_per_day}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Debug Info */}
                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Debug Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-xs font-mono">
                      <div>
                        <span className="text-muted-foreground">winlose_obj type: </span>
                        <span>{typeof selectedWinlose.winlose_obj}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">winlose_obj.data exists: </span>
                        <span>{selectedWinlose.winlose_obj?.data ? 'Yes' : 'No'}</span>
                      </div>
                      {selectedWinlose.winlose_obj?.data && (
                        <div>
                          <span className="text-muted-foreground">Game keys count: </span>
                          <span>{Object.keys(selectedWinlose.winlose_obj.data).length}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">sum_wl (from field): </span>
                        <span>{selectedWinlose.sum_wl}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">sum_vl (from field): </span>
                        <span>{selectedWinlose.sum_vl}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* winlose_obj Structure */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Gamepad2 className="w-4 h-4" />
                      winlose_obj.data (รายละเอียดตามเกม)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedWinlose.winlose_obj?.data ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(selectedWinlose.winlose_obj.data).map(([gameKey, gameData]: [string, any]) => {
                          const gameInfo = GAME_GROUPS.find(g => g.value === gameKey)
                          return (
                            <Card key={gameKey} className="border-zinc-200/50 dark:border-zinc-800">
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-sm">{gameInfo?.label || gameKey}</span>
                                  <Badge 
                                    variant="outline" 
                                    className={cn(
                                      "text-xs",
                                      (gameData?.wl || 0) >= 0 ? "text-emerald-600 border-emerald-500/30" : "text-red-600 border-red-500/30"
                                    )}
                                  >
                                    {(gameData?.wl || 0) >= 0 ? '+' : ''}{formatNumber(gameData?.wl || 0)}
                                  </Badge>
                                </div>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">WL:</span>
                                    <span className={cn(
                                      "font-mono font-bold",
                                      (gameData?.wl || 0) >= 0 ? "text-emerald-600" : "text-red-600"
                                    )}>
                                      {formatNumber(gameData?.wl || 0)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">VL:</span>
                                    <span className="font-mono text-blue-600">{formatNumber(gameData?.vl || 0)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">COM:</span>
                                    <span className="font-mono text-amber-600">{formatNumber(gameData?.com || 0)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Stake:</span>
                                    <span className="font-mono text-purple-600">{formatNumber(gameData?.stake || 0)}</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">ไม่มีข้อมูล winlose_obj.data</p>
                    )}
                  </CardContent>
                </Card>

                {/* Raw JSON View */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Raw JSON (winlose_obj)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px] w-full rounded-md border p-4 bg-muted/30">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                        {JSON.stringify(selectedWinlose.winlose_obj, null, 2)}
                      </pre>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Full Object JSON */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Full Object JSON
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px] w-full rounded-md border p-4 bg-muted/30">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                        {JSON.stringify(selectedWinlose, null, 2)}
                      </pre>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

    </div>
  )
}
